/* ILS — TASK-2256 (initial) / TASK-2287 (all-anchors fix) / TASK-2313 —
 * anchor links with autofocus. Track-your-order remains the REFERENCE
 * behaviour (browser-native scroll to an in-DOM id, plus a JS
 * autofocus of the input inside the target block). Every other anchor
 * in this file uses the same shape: a real DOM `id` on the target so
 * the browser scrolls natively + JS assist for overlays / focus.
 *
 * When the page loads (or the hash changes) with:
 *   #track-order  — browser scrolls to <div id="track-order"> in the footer.
 *                   JS focuses the tracking-code input.
 *   #site-search  — no static id (overlay). JS scrolls to top, opens the
 *                   .ils-suggest overlay (body.ils-suggest-open), and
 *                   focuses the input inside that panel. TASK-2313 fixed
 *                   the "opens then collapses back" race with the header's
 *                   scroll-to-top collapse handler in custom-overrides.js.
 *   #newsletter   — browser scrolls to <section id="newsletter"> on the
 *                   homepage. JS focuses the email input. On pages where
 *                   the section is not rendered — no-op.
 *   #chat         — browser scrolls to <a id="chat"> in the footer. JS
 *                   fires a synthetic click on it so the placeholder popup
 *                   (custom-chat-placeholder-popup.js) opens automatically.
 *   #header       — browser scrolls to <header id="header">. No JS focus.
 *                   Added TASK-2313 for the FAQ "how to switch language"
 *                   answer.
 *   #footer       — browser scrolls to <footer id="footer">. No JS focus.
 *                   Added TASK-2313 for the FAQ "how to switch country"
 *                   answer.
 *
 * Cold-arrival focus resilience:
 *   The browser scrolls to the anchor target, then a chain of app scripts
 *   (Klaviyo popup, header collapse, Rubik mount, etc.) fires during the
 *   next ~1.5s. Some of them touch layout / focus in ways that can drop a
 *   just-set input focus. Rather than fight for the initial gesture window,
 *   we schedule multiple focus attempts on DOMContentLoaded, on window.load,
 *   after a short delay, and after a longer one — so at least the LAST
 *   attempt lands after the noise has settled. If the input is already
 *   focused when an attempt runs, we no-op.
 *
 * TASK-2313 notes:
 *   - `#site-search` stability fix: while the anchor sequence is active
 *     we set `window.__ilsSuggestAnchorSticky = true` so custom-overrides.js
 *     onScroll does NOT remove `ils-suggest-open` when the smooth-scroll to
 *     top crosses the "expand collapsed header" threshold. Sticky is cleared
 *     on backdrop / escape / lens toggle (handled in custom-overrides.js),
 *     or after 4 s as a safety net.
 *   - `#header` / `#footer` piggy-back on browser-native anchor scrolling.
 *     Only added as dispatch cases so `hashchange` on same page still works.
 */
(function () {
  'use strict';

  var TRACK_HASH = '#track-order';
  var SEARCH_HASH = '#site-search';
  var NEWSLETTER_HASH = '#newsletter';
  var CHAT_HASH = '#chat';
  var HEADER_HASH = '#header';
  var FOOTER_HASH = '#footer';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  function tryFocus(el) {
    if (!el) return false;
    if (document.activeElement === el) return true;
    try { el.focus({ preventScroll: true }); }
    catch (_) { el.focus(); }
    return document.activeElement === el;
  }

  function focusWithRetry(selectorFn, attempts, interval) {
    attempts = attempts || 30;
    interval = interval || 80;
    var tries = 0;
    (function step() {
      var el = selectorFn();
      if (el && tryFocus(el)) return;
      tries++;
      if (tries < attempts) setTimeout(step, interval);
    })();
  }

  function scrollTo(el, opts) {
    if (!el) return;
    var behavior = opts && opts.instant ? 'auto' : 'smooth';
    try {
      el.scrollIntoView({ behavior: behavior, block: 'center' });
    } catch (_) {
      var y = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo(0, y);
    }
  }

  var trackSelector = function () {
    var block = document.getElementById('track-order');
    return block ? block.querySelector('.ils-track__input') : null;
  };

  var suggestSelector = function () {
    // Defensive: try the primary current-DOM selector, then fall back to
    // any input inside .ils-suggest, and finally any search input inside
    // it. Survives class renames inside the search section.
    return (
      document.querySelector('.ils-suggest .ils-search input[type="search"]') ||
      document.querySelector('.ils-suggest form.ils-search input') ||
      document.querySelector('.ils-suggest input[type="search"]') ||
      document.querySelector('.ils-suggest input[name="q"]')
    );
  };

  var newsletterBlock = function () {
    // Primary: the home-page section (now id="newsletter"). Fallbacks:
    // the class selector (survives id removal), the footer email form
    // (only if the section is not on the current page and a footer form
    // exists — otherwise no-op).
    return (
      document.getElementById('newsletter') ||
      document.querySelector('.ils-email-sec') ||
      document.querySelector('.ils-footer__email-form') ||
      null
    );
  };

  var newsletterInput = function () {
    var block = newsletterBlock();
    if (!block) return null;
    return (
      block.querySelector('input[type="email"]') ||
      block.querySelector('input[name="contact[email]"]') ||
      block.querySelector('input')
    );
  };

  var chatTrigger = function () {
    return (
      document.querySelector('[data-ils-chat-trigger]') ||
      document.getElementById('chat') ||
      document.querySelector('.ils-footer__chat')
    );
  };

  function handleTrackOrder() {
    if (!document.getElementById('track-order')) return;
    focusWithRetry(trackSelector);
  }

  function handleSiteSearch() {
    // Sticky flag guards the ils-suggest-open class against the
    // scroll-to-top collapse handler in custom-overrides.js onScroll.
    // Cleared on user close (backdrop / escape / lens) or after 4 s.
    window.__ilsSuggestAnchorSticky = true;
    setTimeout(function () { window.__ilsSuggestAnchorSticky = false; }, 4000);
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
    document.body.classList.add('ils-suggest-open');
    focusWithRetry(suggestSelector);
  }

  function handleNewsletter() {
    var block = newsletterBlock();
    if (!block) return;
    // Scroll target is the input, not the block: the block is tall (badge +
    // heading + sub-copy + input + button + fine print, ~280px), so
    // centering the BLOCK often leaves the input near the viewport edge on
    // small screens. Centering the INPUT itself guarantees the field the
    // user is about to type into sits at the middle of the viewport.
    var input = newsletterInput();
    scrollTo(input || block);
    focusWithRetry(newsletterInput);
  }

  var chatClickFired = false;
  function handleChat() {
    var trigger = chatTrigger();
    if (!trigger) return;
    // Instant scroll (not smooth) — the popup fires 400 ms later and locks
    // body scroll; if we used smooth scroll the CTA would still be
    // mid-animation when the popup covers it, and dismissing the popup
    // would leave the user parked far above the CTA. Instant scroll
    // guarantees the CTA sits at the viewport center by the time the
    // popup renders and by the time the user dismisses it.
    scrollTo(trigger, { instant: true });
    if (chatClickFired) return;
    chatClickFired = true;
    setTimeout(function () {
      var t = chatTrigger();
      if (t && typeof t.click === 'function') {
        try { t.click(); } catch (_) {}
      }
    }, 400);
  }

  function handleHeader() {
    // Browser scrolls natively to <header id="header">; JS assist is a
    // fallback for hashchange races where the native scroll was blocked
    // by an in-progress animation on the previous section. Instant, no
    // focus — the target is a landmark, not a field.
    var el = document.getElementById('header');
    if (el) {
      scrollTo(el, { instant: true });
    } else {
      try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }
      catch (_) { window.scrollTo(0, 0); }
    }
  }

  function handleFooter() {
    var el = document.getElementById('footer');
    if (el) {
      scrollTo(el, { instant: true });
    } else {
      var h = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
      try { window.scrollTo({ top: h, left: 0, behavior: 'auto' }); }
      catch (_) { window.scrollTo(0, h); }
    }
  }

  function dispatch() {
    var hash = (window.location.hash || '').toLowerCase();
    if (hash === TRACK_HASH) {
      handleTrackOrder();
    } else if (hash === SEARCH_HASH) {
      handleSiteSearch();
    } else if (hash === NEWSLETTER_HASH) {
      handleNewsletter();
    } else if (hash === CHAT_HASH) {
      handleChat();
    } else if (hash === HEADER_HASH) {
      handleHeader();
    } else if (hash === FOOTER_HASH) {
      handleFooter();
    }
  }

  function reassert() {
    // Called on window.load and after a couple of long delays — the panel
    // stays open / input stays focused unless something else took focus.
    var hash = (window.location.hash || '').toLowerCase();
    if (hash === TRACK_HASH) {
      var input = trackSelector();
      if (input && document.activeElement !== input) tryFocus(input);
    } else if (hash === SEARCH_HASH) {
      if (!document.body.classList.contains('ils-suggest-open')) {
        document.body.classList.add('ils-suggest-open');
      }
      var s = suggestSelector();
      if (s && document.activeElement !== s) tryFocus(s);
    } else if (hash === NEWSLETTER_HASH) {
      var n = newsletterInput();
      if (n) {
        // The homepage loads lazy images / dynamic sections after the initial
        // scroll, which push the newsletter block DOWN over the first ~1.5 s
        // (verified in TASK-2287 audit). Re-scroll during each reassert to
        // keep the input in the viewport center as layout settles.
        var r = n.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var off = Math.abs(r.top - vh / 2);
        if (off > 80) scrollTo(n);
        if (document.activeElement !== n) tryFocus(n);
      }
    }
    // #chat / #header / #footer are one-shot — don't re-scroll / re-open.
  }

  ready(function () {
    if (window.location.hash) {
      requestAnimationFrame(dispatch);
    }
  });

  window.addEventListener('load', function () {
    if (window.location.hash) reassert();
    // Give overlay scripts (Klaviyo, preorder popup, etc.) a chance to render
    // and settle, then re-assert focus one more time. Two delays: 800 ms and
    // 1800 ms — the second one usually catches Klaviyo-triggered blurs.
    setTimeout(reassert, 800);
    setTimeout(reassert, 1800);
  });

  window.addEventListener('hashchange', function () {
    // Allow #chat to re-fire on a fresh hashchange (user navigated away and
    // came back), otherwise it'd stay locked after the first popup dismiss.
    chatClickFired = false;
    dispatch();
  });
})();
